import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { formatEuro } from "../lib/format";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Mountain, Snowflake, Sparkles, Award } from "lucide-react";
import { Link } from "react-router-dom";

export default function Strategy() {
  const [extra, setExtra] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (val) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/strategy/simulate`, { params: { extra_monthly: val } });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
  }, []);

  // Debounce slider
  useEffect(() => {
    const t = setTimeout(() => load(extra), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [extra]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const a = data.avalanche.timeline;
    const s = data.snowball.timeline;
    const max = Math.max(a.length, s.length);
    const rows = [];
    for (let i = 0; i < max; i++) {
      rows.push({
        month: i,
        avalanche: a[i]?.balance ?? 0,
        snowball: s[i]?.balance ?? 0,
      });
    }
    return rows;
  }, [data]);

  if (loading && !data) {
    return <div className="text-[#8B9389] tracking-[0.2em] uppercase text-xs">Calculating…</div>;
  }
  if (!data) return null;

  const { avalanche, snowball, comparison, debt_count, total_balance, total_minimum } = data;
  const noDebts = debt_count === 0;
  const recommended =
    avalanche.feasible && snowball.feasible
      ? comparison.months_saved_with_avalanche > 0 || comparison.interest_saved_with_avalanche > 0
        ? "avalanche"
        : "snowball"
      : avalanche.feasible
      ? "avalanche"
      : "snowball";

  return (
    <div className="space-y-8 fade-in">
      <div>
        <p className="label-kicker">Strategy</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
          Avalanche vs Snowball
        </h1>
        <p className="text-[#5C635A] mt-3 max-w-2xl">
          Add an extra monthly amount on top of your planned installments and see which payoff order
          finishes faster — by interest rate (Avalanche) or by smallest balance (Snowball).
        </p>
      </div>

      {noDebts ? (
        <div className="card-soft p-10 text-center">
          <p className="text-[#5C635A]">
            Add at least one debt with a balance and a monthly installment to run the simulation.
          </p>
          <Button asChild className="mt-4 rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white">
            <Link to="/debts">Go to debts</Link>
          </Button>
        </div>
      ) : (
        <>
          <section className="card-soft p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div className="md:col-span-2">
                <Label className="text-xs tracking-widest uppercase text-[#8B9389]">
                  Extra payment per month (€)
                </Label>
                <div className="mt-3 flex items-center gap-4">
                  <Slider
                    value={[extra]}
                    min={0}
                    max={Math.max(2000, Math.round(total_balance * 0.1))}
                    step={10}
                    onValueChange={(v) => setExtra(v[0])}
                    className="flex-1"
                    data-testid="strategy-extra-slider"
                  />
                  <Input
                    type="number"
                    min="0"
                    step="10"
                    value={extra}
                    onChange={(e) => setExtra(Number(e.target.value || 0))}
                    className="w-32 rounded-xl border-[#E5E3DB] num"
                    data-testid="strategy-extra-input"
                  />
                </div>
                <p className="text-xs text-[#8B9389] mt-3">
                  You&apos;ll be paying <span className="font-medium text-[#1D3328]">{formatEuro(total_minimum + extra)}</span>{" "}
                  per month total ({formatEuro(total_minimum)} minimums + {formatEuro(extra)} extra) across{" "}
                  {debt_count} active debt{debt_count !== 1 ? "s" : ""}.
                </p>
              </div>
              <div className="card-soft p-5 bg-[#F2EFE5] border-[#E5DCC5]">
                <div className="flex items-center gap-2 text-[#1D3328]">
                  <Award className="w-4 h-4" strokeWidth={1.7} />
                  <p className="label-kicker text-[#1D3328]">Recommended</p>
                </div>
                <div className="font-display text-2xl font-medium text-[#1D3328] mt-2 capitalize">
                  {recommended}
                </div>
                {avalanche.feasible && snowball.feasible && comparison.months_saved_with_avalanche !== 0 && (
                  <p className="text-xs text-[#5C635A] mt-1">
                    {comparison.months_saved_with_avalanche > 0
                      ? `Avalanche finishes ${comparison.months_saved_with_avalanche} mo earlier`
                      : `Snowball finishes ${-comparison.months_saved_with_avalanche} mo earlier`}{" "}
                    & saves {formatEuro(Math.abs(comparison.interest_saved_with_avalanche))} in interest.
                  </p>
                )}
                {(!avalanche.feasible || !snowball.feasible) && (
                  <p className="text-xs text-[#B04A32] mt-1">
                    Some debts won&apos;t be paid off — installments may be lower than monthly interest.
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <StrategyCard
              title="Avalanche"
              icon={<Mountain className="w-5 h-5" strokeWidth={1.7} />}
              description="Attack the highest interest rate first. Mathematically optimal."
              accent="#2C4A3B"
              data={avalanche}
              recommended={recommended === "avalanche"}
              testId="avalanche-card"
            />
            <StrategyCard
              title="Snowball"
              icon={<Snowflake className="w-5 h-5" strokeWidth={1.7} />}
              description="Knock out the smallest balance first. Builds momentum & motivation."
              accent="#E07A5F"
              data={snowball}
              recommended={recommended === "snowball"}
              testId="snowball-card"
            />
          </section>

          <section className="card-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="label-kicker">Side by side</p>
                <h3 className="font-display text-xl text-[#1D3328] mt-1 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#C89B6A]" strokeWidth={1.7} />
                  Total balance over time
                </h3>
              </div>
            </div>
            <div className="h-72 min-h-[18rem] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEBE2" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "#8B9389" }}
                    label={{ value: "month", position: "insideBottom", offset: -2, fill: "#8B9389", fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#8B9389" }}
                    tickFormatter={(v) => `€${Math.round(v)}`}
                  />
                  <Tooltip
                    formatter={(v) => formatEuro(v)}
                    contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="avalanche" stroke="#2C4A3B" strokeWidth={2} dot={false} name="Avalanche" />
                  <Line type="monotone" dataKey="snowball" stroke="#E07A5F" strokeWidth={2} dot={false} name="Snowball" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StrategyCard({ title, icon, description, accent, data, recommended, testId }) {
  return (
    <div
      className={`card-soft p-6 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        recommended ? "ring-2 ring-offset-2 ring-[#829A88]" : ""
      }`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: `${accent}18`, color: accent }}
          >
            {icon}
          </div>
          <h3 className="font-display text-2xl font-medium mt-3" style={{ color: accent }}>
            {title}
          </h3>
          <p className="text-sm text-[#5C635A] mt-1 max-w-xs">{description}</p>
        </div>
        {recommended && (
          <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white bg-[#2C4A3B] px-2 py-1 rounded-md">
            Pick
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6">
        <Stat label="Months" value={data.feasible ? data.total_months : "∞"} />
        <Stat label="Total interest" value={formatEuro(data.total_interest)} />
        <Stat label="Total paid" value={formatEuro(data.total_paid)} />
      </div>

      {data.payoff_order.length > 0 && (
        <div className="mt-6">
          <p className="label-kicker">Payoff order</p>
          <ol className="mt-3 space-y-1.5">
            {data.payoff_order.map((p, idx) => (
              <li
                key={p.id}
                className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg bg-[#FAF8F2]"
              >
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-white border border-[#E5E3DB] text-[10px] font-medium flex items-center justify-center text-[#5C635A]">
                    {idx + 1}
                  </span>
                  <span className="font-mono text-xs text-[#8B9389]">{p.code}</span>
                  <span className="text-[#1D3328] font-medium truncate max-w-[10rem]">
                    {p.owed_to}
                  </span>
                </span>
                <span className="text-xs text-[#5C635A]">
                  month {p.paid_off_month}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {!data.feasible && (
        <p className="text-xs text-[#B04A32] mt-4 bg-[#F7E6DF] border border-[#E07A5F]/30 rounded-lg px-3 py-2">
          Some debts can&apos;t be paid off at this pace — installments may be lower than the monthly
          interest. Try increasing the extra payment.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.22em] uppercase text-[#8B9389]">{label}</p>
      <p className="text-base font-medium num text-[#1D3328] mt-0.5">{value}</p>
    </div>
  );
}
